import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Mark, Extension } from "@tiptap/core";
import { useEffect, useState } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Minus, Eraser,
  IndentIncrease, IndentDecrease, Palette, Highlighter, Type, Code, Eye,
} from "lucide-react";
import { useI18n } from "@/i18n/i18n";

// ---------- Custom Indent extension (works on paragraphs + headings) ----------
const INDENT_STEP = 32;
const MAX_INDENT = 8;
const Indent = Extension.create({
  name: "indent",
  addOptions() { return { types: ["paragraph", "heading"], indentStep: INDENT_STEP, maxIndent: MAX_INDENT }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        indent: {
          default: 0,
          parseHTML: (el) => {
            const px = parseInt(el.style.paddingLeft || "0", 10);
            return px ? Math.round(px / INDENT_STEP) : 0;
          },
          renderHTML: (attrs) => {
            const level = Math.min(Math.max(attrs.indent || 0, 0), MAX_INDENT);
            return level ? { style: `padding-left: ${level * INDENT_STEP}px` } : {};
          },
        },
      },
    }];
  },
  addCommands() {
    const setIndent = (delta) => ({ state, tr, dispatch }) => {
      const { from, to } = state.selection;
      let changed = false;
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!this.options.types.includes(node.type.name)) return;
        const cur = node.attrs.indent || 0;
        const next = Math.min(Math.max(cur + delta, 0), this.options.maxIndent);
        if (next !== cur) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
          changed = true;
        }
      });
      if (changed && dispatch) dispatch(tr);
      return changed;
    };
    return {
      indentBlock: () => setIndent(+1),
      outdentBlock: () => setIndent(-1),
    };
  },
});

// ---------- Custom FontSize mark ----------

const FontSize = Mark.create({
  name: "fontSize",
  addOptions() { return { types: ["textStyle"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize?.replace(/['"]/g, "") || null,
          renderHTML: (attrs) => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// ---------- Presets ----------
const FONT_SIZES = [
  { label: "Pequeño", value: "14px" },
  { label: "Normal", value: "17px" },
  { label: "Grande", value: "20px" },
  { label: "Gigante", value: "26px" },
];
const TEXT_COLORS = ["#0f172a", "#004aad", "#dc2626", "#059669", "#d97706", "#7c3aed", "#db2777", "#0891b2"];
const HIGHLIGHT_COLORS = ["#fef3c7", "#fee2e2", "#dcfce7", "#dbeafe", "#f3e8ff", "#fce7f3", "#e0f2fe", "#ffffff"];

const btnBase = "h-8 min-w-8 px-2 rounded-md flex items-center justify-center transition-all text-slate-600 hover:bg-slate-100";
const btnActive = "bg-brand-blue text-white hover:bg-brand-blue/90 shadow-sm";

function ToolbarBtn({ onClick, active, title, children, testId }) {
  return (
    <button type="button" title={title} data-testid={testId} onClick={onClick}
      className={`${btnBase} ${active ? btnActive : ""}`}>
      {children}
    </button>
  );
}

function ColorSwatch({ color, onClick, active, title, testId }) {
  return (
    <button type="button" title={title} data-testid={testId} onClick={onClick}
      className={`h-5 w-5 rounded border ${active ? "ring-2 ring-brand-blue ring-offset-1" : "border-slate-300"} hover:scale-110 transition-transform`}
      style={{ background: color }} />
  );
}

export default function RichTextEditor({ value, onChange, placeholder, testId = "rich-editor" }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("visual"); // "visual" | "html"
  const [htmlDraft, setHtmlDraft] = useState(value || "");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ inline: false, HTMLAttributes: { style: "max-width:100%;height:auto;border-radius:6px;" } }),
      TextAlign.configure({ types: ["heading", "paragraph"], alignments: ["left", "center", "right", "justify"] }),
      Placeholder.configure({ placeholder: placeholder || "" }),
      TextStyle,
      FontSize,
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      Indent,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
    editorProps: {
      handlePaste: (view, event) => {
        // Strip Google Docs / Word junk on paste — keep plain text with basic formatting.
        const html = event.clipboardData?.getData("text/html");
        if (html && (html.includes("docs-internal-guid") || html.includes("MsoNormal"))) {
          event.preventDefault();
          const text = event.clipboardData?.getData("text/plain") || "";
          view.dispatch(view.state.tr.insertText(text));
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || "", false);
    }
    setHtmlDraft(value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // When switching from HTML → Visual, push the edited HTML into the editor
  const switchToVisual = () => {
    if (mode === "html" && editor) {
      editor.commands.setContent(htmlDraft || "", false);
      onChange && onChange(htmlDraft || "");
    }
    setMode("visual");
  };
  const switchToHtml = () => {
    if (editor) setHtmlDraft(editor.getHTML());
    setMode("html");
  };

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt(t("template_editor_link_prompt"), prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const promptImage = () => {
    const url = window.prompt(t("template_editor_image_prompt"), "https://");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };
  const changeFontSize = (size) => {
    if (!size || size === "unset") editor.chain().focus().unsetFontSize().run();
    else editor.chain().focus().setFontSize(size).run();
  };
  const setColor = (color) => editor.chain().focus().setColor(color).run();
  const setHighlight = (color) => color === "#ffffff"
    ? editor.chain().focus().unsetHighlight().run()
    : editor.chain().focus().setHighlight({ color }).run();
  const indent = () => {
    // 1) If inside a list, sink the list item (nested indent)
    if (editor.isActive("bulletList") || editor.isActive("orderedList")) {
      editor.chain().focus().sinkListItem("listItem").run();
      return;
    }
    // 2) Otherwise, indent the paragraph/heading via padding-left
    editor.chain().focus().indentBlock().run();
  };
  const outdent = () => {
    if (editor.isActive("bulletList") || editor.isActive("orderedList")) {
      editor.chain().focus().liftListItem("listItem").run();
      return;
    }
    editor.chain().focus().outdentBlock().run();
  };
  const clearFormat = () => editor.chain().focus().unsetAllMarks().clearNodes().run();
  const currentSize = editor.getAttributes("textStyle").fontSize || "";

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={testId}>
      {/* Mode tabs */}
      <div className="flex items-center gap-1 px-2 pt-2 border-b border-slate-100">
        <button
          type="button"
          onClick={switchToVisual}
          className={`px-3 py-1.5 text-xs font-medium rounded-t-md flex items-center gap-1.5 transition-colors ${mode === "visual" ? "bg-slate-50 text-slate-900 border-x border-t border-slate-200 -mb-px" : "text-slate-500 hover:text-slate-700"}`}
          data-testid={`${testId}-tab-visual`}
        >
          <Eye className="h-3.5 w-3.5" />
          {t("editor_tab_visual")}
        </button>
        <button
          type="button"
          onClick={switchToHtml}
          className={`px-3 py-1.5 text-xs font-medium rounded-t-md flex items-center gap-1.5 transition-colors ${mode === "html" ? "bg-slate-50 text-slate-900 border-x border-t border-slate-200 -mb-px" : "text-slate-500 hover:text-slate-700"}`}
          data-testid={`${testId}-tab-html`}
        >
          <Code className="h-3.5 w-3.5" />
          {t("editor_tab_html")}
        </button>
      </div>
      {mode === "html" ? (
        <textarea
          value={htmlDraft}
          onChange={(e) => { setHtmlDraft(e.target.value); onChange && onChange(e.target.value); }}
          spellCheck={false}
          className="w-full min-h-[400px] px-4 py-3 font-mono text-[13px] leading-[1.6] text-slate-800 bg-slate-50 focus:outline-none resize-y"
          data-testid={`${testId}-html-textarea`}
          placeholder="<p>Escribe HTML aquí…</p>"
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-50 items-center sticky top-0 z-10">
            {/* Basic formatting */}
        <ToolbarBtn testId={`${testId}-bold`} title={`${t("template_editor_bold")} (Ctrl+B)`} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-italic`} title={`${t("template_editor_italic")} (Ctrl+I)`} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-underline`} title={`${t("template_editor_underline")} (Ctrl+U)`} active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarBtn>

        <div className="w-px bg-slate-200 mx-1 h-6" />

        {/* Font size */}
        <select
          value={currentSize}
          onChange={(e) => changeFontSize(e.target.value)}
          className="h-8 px-2 border border-slate-200 rounded-md text-xs bg-white hover:bg-slate-100 cursor-pointer"
          title={t("editor_font_size")}
          data-testid={`${testId}-fontsize`}
        >
          <option value="">{t("editor_font_size_default")}</option>
          {FONT_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {/* Headings */}
        <ToolbarBtn testId={`${testId}-h1`} title={t("template_editor_h1")} active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-h2`} title={t("template_editor_h2")} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-h3`} title={t("template_editor_h3")} active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" />
        </ToolbarBtn>

        <div className="w-px bg-slate-200 mx-1 h-6" />

        {/* Text color */}
        <div className="flex items-center gap-1 px-1 py-0.5 border border-slate-200 rounded-md bg-white" title={t("editor_text_color")}>
          <Palette className="h-3.5 w-3.5 text-slate-500 mr-1" />
          {TEXT_COLORS.map((c) => (
            <ColorSwatch key={c} color={c} onClick={() => setColor(c)}
              active={editor.getAttributes("textStyle").color === c}
              testId={`${testId}-color-${c}`} title={c} />
          ))}
        </div>

        {/* Highlight */}
        <div className="flex items-center gap-1 px-1 py-0.5 border border-slate-200 rounded-md bg-white" title={t("editor_highlight_color")}>
          <Highlighter className="h-3.5 w-3.5 text-slate-500 mr-1" />
          {HIGHLIGHT_COLORS.map((c) => (
            <ColorSwatch key={c} color={c} onClick={() => setHighlight(c)}
              active={editor.getAttributes("highlight").color === c}
              testId={`${testId}-hl-${c}`} title={c === "#ffffff" ? "clear" : c} />
          ))}
        </div>

        <div className="w-px bg-slate-200 mx-1 h-6" />

        {/* Lists + indent */}
        <ToolbarBtn testId={`${testId}-bullet`} title={t("template_editor_bullet")} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-ordered`} title={t("template_editor_ordered")} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-outdent`} title={t("editor_outdent")} onClick={outdent}>
          <IndentDecrease className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-indent`} title={t("editor_indent")} onClick={indent}>
          <IndentIncrease className="h-4 w-4" />
        </ToolbarBtn>

        <div className="w-px bg-slate-200 mx-1 h-6" />

        {/* Link / image / hr */}
        <ToolbarBtn testId={`${testId}-link`} title={`${t("template_editor_link")} (Ctrl+K)`} active={editor.isActive("link")} onClick={promptLink}>
          <LinkIcon className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-image`} title={t("template_editor_image")} onClick={promptImage}>
          <ImageIcon className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-hr`} title={t("editor_horizontal_rule")} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="h-4 w-4" />
        </ToolbarBtn>

        <div className="w-px bg-slate-200 mx-1 h-6" />

        {/* Alignment */}
        <ToolbarBtn testId={`${testId}-align-left`} title={t("template_editor_left")} active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-align-center`} title={t("template_editor_center")} active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenter className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-align-right`} title={t("template_editor_right")} active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn testId={`${testId}-align-justify`} title={t("template_editor_justify")} active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
          <AlignJustify className="h-4 w-4" />
        </ToolbarBtn>

        <div className="w-px bg-slate-200 mx-1 h-6" />

        <ToolbarBtn testId={`${testId}-clear`} title={t("editor_clear_format")} onClick={clearFormat}>
          <Eraser className="h-4 w-4" />
        </ToolbarBtn>
      </div>

      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-4 py-3 min-h-[240px] focus:outline-none [&_.ProseMirror]:min-h-[220px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:text-[17px] [&_.ProseMirror]:leading-[1.65] [&_.ProseMirror_p]:my-2 [&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child]:before:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left [&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child]:before:h-0 [&_.ProseMirror_a]:text-brand-blue [&_.ProseMirror_a]:underline [&_.ProseMirror_hr]:my-4 [&_.ProseMirror_hr]:border-slate-200"
        data-testid={`${testId}-content`}
      />
        </>
      )}
    </div>
  );
}
